import { useMemo, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { useCitiesByState, useStates } from '@alamo/api';
import type { City } from '@alamo/types';
import { EntitySelect, type EntitySelectOption } from '@alamo/ui';

export interface GeographicCitySelectProps {
  /** UF (2-letter, e.g. "SP") OR a State UUID. The component resolves UUIDs
   *  to UF via the `useStates()` cache to keep the API consistent for callers
   *  that already have a UUID-shaped form value. Disabled when both are null. */
  stateUf?: string | null;
  stateId?: string | null;
  label?: string;
  placeholder?: string;
  value: string | null;
  onChange: (id: string | null, item: City | null) => void;
  required?: boolean;
  disabled?: boolean;
  error?: string;
  helperText?: string;
  className?: string;
}

const SEARCH_LIMIT = 50;

export function GeographicCitySelect({
  stateUf,
  stateId,
  label,
  placeholder,
  value,
  onChange,
  required,
  disabled,
  error,
  helperText,
  className,
}: GeographicCitySelectProps) {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');

  // Resolve stateId → UF (locations data is heavily cached so this is free).
  const { data: states } = useStates();
  const resolvedUf = useMemo(() => {
    if (stateUf) return stateUf;
    if (!stateId) return undefined;
    return states?.find((s) => s.id === stateId)?.uf;
  }, [stateUf, stateId, states]);

  const enabledQuery = !!resolvedUf && !disabled;
  const { data, isFetching } = useCitiesByState(
    enabledQuery ? resolvedUf : undefined,
    search || undefined,
    SEARCH_LIMIT,
  );

  const options: EntitySelectOption<City>[] = useMemo(() => {
    if (!enabledQuery) return [];
    return (data ?? []).map((c) => ({
      value: c.id,
      label: c.name,
      hint: c.state?.uf ?? resolvedUf,
      data: c,
    }));
  }, [data, enabledQuery, resolvedUf]);

  const isDisabled = disabled || !resolvedUf;
  const disabledReason = !resolvedUf ? t('common:entity_select.requires_state') : undefined;

  return (
    <EntitySelect<City>
      label={label ?? t('common:entity_select.city_label')}
      placeholder={placeholder ?? t('common:entity_select.city_placeholder')}
      value={value}
      onChange={onChange}
      options={options}
      onSearch={enabledQuery ? setSearch : undefined}
      loading={isFetching && enabledQuery}
      loadingMessage={t('common:entity_select.loading')}
      emptyMessage={t('common:entity_select.no_results')}
      required={required}
      disabled={isDisabled}
      disabledReason={disabledReason}
      error={error}
      helperText={helperText}
      className={className}
    />
  );
}
